import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Filter } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const [tours, setTours] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = [
    { value: 'all', label: 'Todos' },
    { value: 'Imobiliário', label: 'Imobiliário' },
    { value: 'Hotéis', label: 'Hotéis' },
    { value: 'Eventos', label: 'Eventos' },
    { value: 'Restaurantes', label: 'Restaurantes' },
    { value: 'Escolas', label: 'Escolas' },
    { value: 'Concessionárias', label: 'Concessionárias' },
  ];

  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        const url = selectedCategory === 'all'
          ? `${API}/tours`
          : `${API}/tours?category=${selectedCategory}`;
        const response = await axios.get(url);
        setTours(response.data);
      } catch (error) {
        console.error('Erro ao carregar tours:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [selectedCategory]);

  return (
    <div className="min-h-screen bg-white pt-32 pb-24" data-testid="portfolio-page">
      <SEO 
        title="Portfólio de Tours Virtuais 360° | Gran Tour 360"
        description="Explore nosso portfólio de tours virtuais 360° para imobiliárias, hotéis, restaurantes, concessionárias e empresas. Experiências imersivas que convertem."
        url="https://grantour360.com.br/portfolio"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#FF3366] text-xs uppercase tracking-[0.2em] font-semibold mb-4">Portfólio</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Nossos Tours Virtuais 360°
            </h1>
            <p className="text-gray-500 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
              Cada projeto é uma história visual. Navegue e descubra como apresentamos o valor real de cada espaço.
            </p>
          </motion.div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="category-filter">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-6 py-2.5 rounded-md font-medium transition-all ${
                selectedCategory === category.value
                  ? 'bg-[#FF3366] text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
              }`}
              data-testid={`filter-${category.value}`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-24">
            <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-[#FF3366] rounded-full animate-spin"></div>
            <p className="text-gray-500 mt-4">Carregando tours...</p>
          </div>
        ) : tours.length === 0 ? (
          <div className="text-center py-24">
            <Filter size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Nenhum tour encontrado para esta categoria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tours.map((tour, index) => (
              <motion.div
                key={tour.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-xl hover:border-[#FF3366]/30 transition-all"
                data-testid={`tour-card-${index}`}
              >
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={tour.image_url}
                    alt={`Tour virtual 360° - ${tour.title} - ${tour.category}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <a
                      href={tour.tour_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-0 group-hover:opacity-100 transition-all px-6 py-3 bg-[#FF3366] text-white font-semibold rounded-md inline-flex items-center gap-2"
                      data-testid={`view-tour-${index}`}
                    >
                      <Eye size={18} />
                      Ver Tour 360°
                    </a>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-[#FF3366] text-xs uppercase tracking-wider mb-2 font-semibold">{tour.category}</p>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{tour.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{tour.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;
